import { Component, Input, Output, EventEmitter, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ReactiveFormsModule } from '@angular/forms';
import { FormConfigService } from '../forms-config.service';
import { AppStateService } from '../../app-state.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

interface SelectedFile {
  file: File;
  fieldName: string;
  docCategory: number;
  description: string;
}
const MAX_FILE_SIZE_MB = 10; // Set maximum file size to 5 MB

@Component({
  selector: 'app-document-upload',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.scss']
})
export class DocumentUploadComponent implements OnInit {
  @Input() metadata: any;
  @Input() fields: any[] = [];
  @Output() fileSelected = new EventEmitter<{ file: File, fieldName: string }>();
  @Output() uploadComplete = new EventEmitter<{ success: boolean, count: number }>();


  selectedFiles: SelectedFile[] = [];
  fileStatus: { [key: string]: string } = {};  // To store the upload status of each file
  formData: any = {};  // Ensure this is populated appropriately elsewhere
  formConfigService = inject(FormConfigService);
  appStateService = inject(AppStateService);
  snackBar = inject(MatSnackBar);

  documentUploadForm: FormGroup;

  // Track existing documents
  existingDocuments: any[] = [];
  loading = true;
  uploading = false;  // Track upload in progress

  constructor(private fb: FormBuilder) {
    this.documentUploadForm = this.fb.group({});
  }

  ngOnInit() {
    this.createForm();
    this.fetchExistingDocuments();
  }

  /**
   * Fetch existing documents for this patient to show what's already uploaded
   */
  private fetchExistingDocuments() {
    const clientId = this.appStateService.getState().clientId || '';
    const patientId = this.appStateService.getState().patientId || '';

    if (!clientId || !patientId) {
      this.loading = false;
      return;
    }

    this.formConfigService.fetchDocuments(clientId, patientId).subscribe({
      next: (response: any) => {
        this.loading = false;
        const allDocs = response.documents || [];

        // Filter documents based on metadata category (driver's license vs insurance)
        if (this.metadata?.title?.toLowerCase().includes('license')) {
          this.existingDocuments = allDocs.filter((doc: any) =>
            doc.description?.toLowerCase().includes('license') ||
            doc.category?.toLowerCase().includes('license') ||
            doc.documentType?.toLowerCase().includes('license')
          );
        } else if (this.metadata?.title?.toLowerCase().includes('insurance')) {
          this.existingDocuments = allDocs.filter((doc: any) =>
            doc.description?.toLowerCase().includes('insurance') ||
            doc.category?.toLowerCase().includes('insurance') ||
            doc.documentType?.toLowerCase().includes('insurance')
          );
        } else {
          this.existingDocuments = allDocs;
        }
      },
      error: (error: any) => {
        this.loading = false;
      }
    });
  }

  /**
   * Check if there are existing documents uploaded
   */
  hasExistingDocuments(): boolean {
    return this.existingDocuments.length > 0;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Dynamically creates form controls based on the provided fields.
   */
  private createForm() {
    this.fields.forEach(field => {
      const validators = field.required ? [Validators.required] : [];
      this.documentUploadForm.addControl(field.name, this.fb.control('', validators));
    });
  }

  async onFileChange(event: any, fieldName: string) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileType = file.type;

      // File size validation
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        this.showError(`File size exceeds ${MAX_FILE_SIZE_MB}MB. Please upload a smaller file.`);
        event.target.value = ''; // Clear the file input
        return;
      }

      if (this.isValidFileType(fileType, fieldName)) {
        // Remove any previous selection for this field
        this.selectedFiles = this.selectedFiles.filter(f => f.fieldName !== fieldName);

        // Reset file status for this field before processing
        this.fileStatus[fieldName] = 'pending'; // Ensure file status is set to 'pending' before processing

        try {
          this.setProcessingStatus(fieldName);
          let processedFile = file;

          if (this.isImageFile(file)) {
            const compressedBase64 = await this.resizeAndCompressImage(file, 800, 90);
            processedFile = this.base64ToFile(compressedBase64, file.name, file.type);
          }

          this.documentUploadForm.patchValue({ [fieldName]: processedFile });
          this.documentUploadForm.get(fieldName)?.updateValueAndValidity();

          this.fileSelected.emit({ file: processedFile, fieldName });
          const docCategory = this.getDocCategory(fieldName);
          const description = this.getDescription(fieldName);

          this.selectedFiles.push({ file: processedFile, fieldName, docCategory, description });
          this.fileStatus[fieldName] = 'pending';
        } catch (error) {
          this.snackBar.open('Error processing file. Please try again.', 'Close', { duration: 3000 });
          this.fileStatus[fieldName] = 'failed';
        }
      } else {
        this.showError(`Invalid file type for ${fieldName}. Please upload a valid file.`);
        event.target.value = '';
      }
    }
  }



  /**
   * Validates the file type based on the field's expected type.
   * @param fileType - MIME type of the file.
   * @param fieldName - The name of the form field.
   * @returns Boolean indicating validity.
   */
  private isValidFileType(fileType: string, fieldName: string): boolean {
    const field = this.fields.find(f => f.name === fieldName);
    if (field) {
      if (field.type === 'image') {
        return ['image/jpeg', 'image/jpg', 'image/png'].includes(fileType);
      }
      if (field.type === 'document') {
        return fileType === 'application/pdf';
      }
    }
    return false;
  }

  getAcceptedFileTypes(fieldType: string): string {
    if (fieldType === 'image') {
      return 'image/jpeg, image/jpg, image/png';
    }
    if (fieldType === 'document') {
      return 'application/pdf';
    }
    return '';
  }

  /**
   * Checks if the provided file is an image.
   * @param file - The file to check.
   * @returns Boolean indicating if the file is an image.
   */
  isImageFile(file: File): boolean {
    return ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
  }

  /**
   * Retrieves the description based on the field name.
   * @param fieldName - The name of the form field.
   * @returns Description string.
   */
  private getDescription(fieldName: string): string {
    const descriptions: { [key: string]: string } = {
      'driversLicenseFront': 'Driver\'s License Front',
      'driversLicenseBack': 'Driver\'s License Back',
      'insuranceCardFront': 'Insurance Card Front',
      'insuranceCardBack': 'Insurance Card Back'
    };
    return descriptions[fieldName] || '';
  }

  /**
   * Determines the document category based on the field name.
   * @param fieldName - The name of the form field.
   * @returns Numeric category.
   */
  private getDocCategory(fieldName: string): number {
    if (fieldName.includes('driversLicense')) return 1;
    if (fieldName.includes('insuranceCard')) return 2;
    return 0;
  }

  /**
   * Resizes and compresses an image using the canvas API.
   * @param file - The image file to process.
   * @param maxDimension - Maximum width or height in pixels.
   * @param quality - Compression quality (0-100).
   * @returns A promise that resolves to a base64 string of the processed image.
   */
  private resizeAndCompressImage(file: File, maxDimension: number, quality: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const img = new Image();

      reader.onload = (event: any) => {
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          let { width, height } = img;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxDimension) {
              height = (height * maxDimension) / width;
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width = (width * maxDimension) / height;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // Draw the resized image onto the canvas
          ctx?.drawImage(img, 0, 0, width, height);

          // Convert the canvas to a Blob (JPEG format)
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              } else {
                reject('Compression failed: Unable to generate image blob.');
              }
            },
            'image/jpeg',
            quality / 100
          );
        };

        img.onerror = () => reject('Error loading image.');
      };

      reader.onerror = () => reject('Error reading file.');
      reader.readAsDataURL(file);
    });
  }


  /**
   * Converts a base64 string back to a File object.
   * @param base64 - The base64 string to convert.
   * @param fileName - The desired file name.
   * @param fileType - The MIME type of the file.
   * @returns The resulting File object.
   */
  private base64ToFile(base64: string, fileName: string, fileType: string): File {
    const arr = base64.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : fileType;
    const bstr = atob(arr[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new File([u8arr], fileName, { type: mime });
  }

  /**
   * Handles the upload of selected files by converting them to base64 and sending to the server.
   */
  async uploadFiles() {
    if (this.selectedFiles.length === 0) {
      this.snackBar.open('No files selected.', 'Close', { duration: 3000 });
      return;
    }

    const formData = new FormData();
    this.selectedFiles.forEach(fileObj => {
      formData.append('documents', fileObj.file, fileObj.file.name);
      formData.append('DocCategory', fileObj.docCategory.toString());
      formData.append('Description', fileObj.description);
      formData.append('DateCreated', new Date().toISOString());
    });

    const clientId = this.appStateService.getState().clientId || '';
    const patientId = this.appStateService.getState().patientId || '';

    try {
      const uploadCount = this.selectedFiles.length;
      this.uploading = true;  // Start loading state
      this.selectedFiles.forEach(fileObj => this.fileStatus[fileObj.fieldName] = 'uploading');

      this.formConfigService.uploadDocument(clientId, patientId, formData)
      .subscribe(
        () => {
          this.uploading = false;  // End loading state
          this.snackBar.open('Documents uploaded successfully!', 'Close', { duration: 4000 });
          this.selectedFiles.forEach(fileObj => this.fileStatus[fileObj.fieldName] = 'uploaded');
          this.uploadComplete.emit({ success: true, count: uploadCount });
          // Refresh the existing documents list
          this.fetchExistingDocuments();
          // Clear selected files after successful upload
          this.selectedFiles = [];
          this.documentUploadForm.reset();
        },
        (error) => {
          this.uploading = false;  // End loading state
          this.snackBar.open('Error uploading documents', 'Close', { duration: 2000 });
          this.selectedFiles.forEach(fileObj => this.fileStatus[fileObj.fieldName] = 'failed');
          this.uploadComplete.emit({ success: false, count: 0 });
        }
      );
    } catch (error) {
      this.uploading = false;  // End loading state
      this.snackBar.open('Error processing upload. Please try again.', 'Close', { duration: 3000 });
      this.uploadComplete.emit({ success: false, count: 0 });
    }
  }


  private convertFilesToBase64(files: SelectedFile[]): Promise<string[]> {
    return Promise.all(files.map(fileObj => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            resolve(reader.result as string);
          } else {
            reject('File reading failed');
          }
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(fileObj.file);
      });
    }));
  }


  private showError(message: string): void {
    this.snackBar.open(message, 'Close', { duration: 3000 });
  }

  private setProcessingStatus(fieldName: string): void {
    this.fileStatus[fieldName] = 'processing';
  }

  isUploading(): boolean {
    return this.uploading || Object.values(this.fileStatus).includes('processing') || Object.values(this.fileStatus).includes('uploading');
  }

  hasFiles(): boolean {
    return this.selectedFiles.length > 0;
  }

  clearAll() {
    this.documentUploadForm.reset();
    this.selectedFiles = [];
    this.fileStatus = {};
  }
}
